import * as React from "react";

import {
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Pagination as ShadcnPagination,
} from "@/components/ui/pagination";
import { createArray, flattenedArray } from "helper/array";
import { isDefined } from "@/helper/utils";
import { cn } from "@/lib/utils";
import { PaginationDot } from "./PaginationDot";
import { PaginationNumber } from "./PaginationNumber";
import { PaginationType, PaginationWithDotsProps } from "@/components/data-table/types/pagination-with-dots-props.type";
import { PageOption, PaginationTypeProps } from "./types/pagination-type-props";

const getPageOptions = (current: number, max: number, maxChunk = 3): Array<PageOption> => {
  if (max <= maxChunk + 2) {
    return createArray(max).map((_, i) => ({ page: i + 1 }));
  }

  if (current <= maxChunk - 1) {
    return flattenedArray([
      createArray(maxChunk).map<PageOption>((_, index) => ({
        page: index + 1,
        optional: index === maxChunk - 1,
      })),
      { page: undefined },
      { page: max },
    ]);
  }

  if (max - current <= maxChunk - 2) {
    return flattenedArray<PageOption>([
      { page: 1 },
      { page: undefined },
      createArray(maxChunk)
        .map((_, index) => ({ page: max - index, optional: index === 0 }))
        .reverse(),
    ]);
  }

  const half = Math.floor(maxChunk / 2);

  return flattenedArray<PageOption>([
    { page: 1 },
    { page: undefined },
    createArray(maxChunk).map((_, index) => ({
      page: current + index - half,
    })),
    { page: undefined },
    { page: max },
  ]);
};

export const Pagination = ({
  totalPage = 0,
  currentPage = 0,
  onChangePage,
  className,
}: Pick<PaginationTypeProps, "totalPage" | "currentPage" | "onChangePage" | "className">) => {
  if (totalPage <= 1) {
    return null;
  }
  const disableNext = currentPage === totalPage;
  const goNext = () => {
    if (disableNext) {
      return;
    }
    onChangePage(currentPage + 1);
  };
  const disablePrev = currentPage <= 1;
  const goPrev = () => {
    if (disablePrev) {
      return;
    }
    onChangePage(currentPage - 1);
  };
  const listPage = getPageOptions(currentPage, totalPage);

  return (
    <ShadcnPagination className={className}>
      <PaginationContent>
        <PaginationItem onClick={goPrev}>
          <PaginationPrevious
            className={cn("cursor-pointer", disablePrev && "pointer-events-none cursor-not-allowed opacity-30")}
          />
        </PaginationItem>
        {listPage.map(({ page }, index) =>
          isDefined(page) ? (
            <PaginationItem onClick={() => onChangePage(page)} key={`page-${page}-${index}`}>
              <PaginationLink className="cursor-pointer" isActive={page === currentPage}>
                {page}
              </PaginationLink>
            </PaginationItem>
          ) : (
            <PaginationItem key={`page-${index}`}>
              <PaginationEllipsis />
            </PaginationItem>
          ),
        )}
        <PaginationItem>
          <PaginationNext
            className={cn("cursor-pointer", disableNext && "pointer-events-none cursor-not-allowed opacity-30")}
            onClick={goNext}
          />
        </PaginationItem>
      </PaginationContent>
    </ShadcnPagination>
  );
};

export const PaginationWithDots = (props: Omit<PaginationWithDotsProps, "perPage" | "onChangePageSize">) => {
  const {
    totalPage = 0,
    currentPage = 0,
    onChangePage,
    className,
    type = "number",
    maxChunk,
    showControl = true,
  } = props;
  if (totalPage <= 1) {
    return null;
  }

  const goNext = () => onChangePage(currentPage + 1);
  const disableNext = currentPage === totalPage;
  const goPrev = () => onChangePage(currentPage - 1);
  const disablePrev = currentPage <= 1;
  const listPage = getPageOptions(currentPage, totalPage, maxChunk);

  const paginationByType: Record<PaginationType, React.ReactElement> = {
    dot: (
      <PaginationDot
        goPrev={goPrev}
        goNext={goNext}
        disablePrev={disablePrev}
        disableNext={disableNext}
        listPage={listPage}
        onChangePage={onChangePage}
        currentPage={currentPage}
        className={className}
        showControl={showControl}
      />
    ),
    number: (
      <PaginationNumber
        goPrev={goPrev}
        goNext={goNext}
        disablePrev={disablePrev}
        disableNext={disableNext}
        listPage={listPage}
        onChangePage={onChangePage}
        currentPage={currentPage}
        className={className}
        showControl={showControl}
      />
    ),
  };
  return paginationByType[type];
};
